import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Search } from 'lucide-react';
import { debounce } from 'lodash-es';
import GeocodingModel from '../../../../api/lifeline/geocoding';
import toast from 'react-hot-toast';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';

const MIN_INPUT_LENGTH = 3;
const DEBOUNCE_DELAY = 500;

export const LocationSearchBox = ({ setCxPinPoint }) => {
  const inputFieldRef = useRef(null);

  const [addressInstruction, setAddressInstruction] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [minInputError, setMinInputError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);

  const FetchPredictions = useMemo(
    () =>
      debounce((debounceInputValue) => {
        if (debounceInputValue.length === 0) {
          setPredictions([]);
          setMinInputError('');
          setAddressInstruction('');
          return;
        }

        if (debounceInputValue.length < MIN_INPUT_LENGTH) {
          setPredictions([]);
          setMinInputError(`Enter at least ${MIN_INPUT_LENGTH} characters`);
          setAddressInstruction('');
          return;
        }

        setMinInputError('');
        setAddressInstruction('');
        setPredictionsLoading(true);
        setPredictions([]);

        GeocodingModel.getAutocompleteSuggestion({
          search_query: debounceInputValue,
        })
          .then((response) => {
            if (response) {
              if (response.suggestions?.length === 0) {
                setAddressInstruction(
                  'No address found. Please try different keywords.',
                );
              }
              setPredictions(response.suggestions || []);
              setSessionToken(response.session_token || null);
            }
            setPredictionsLoading(false);
          })
          .catch(() => {
            toast.error(
              'Failed to fetch address predictions. Please try again.',
            );
            setPredictionsLoading(false);
          });
      }, DEBOUNCE_DELAY),
    [],
  );

  const onInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const trimmedInput = value.trim();

    // If input is empty, immediately clear error and predictions/messages
    if (trimmedInput.length === 0) {
      setMinInputError('');
      setPredictions([]);
      setAddressInstruction('');
      FetchPredictions.cancel();
      return;
    }

    FetchPredictions(trimmedInput);
  };

  const onSelectPrediction = (placeId, description) => {
    setInputValue(description);
    setPredictions([]);

    GeocodingModel.GetPlaceDetails({
      place_id: placeId,
      session_token: sessionToken,
    })
      .then((response) => {
        if (response) {
          setCxPinPoint({
            lat: response.latitude,
            lng: response.longitude,
          });
        }
      })
      .catch(() => {
        toast.error('Failed to fetch address details. Please try again.');
      });
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      FetchPredictions.cancel();
    };
  }, [FetchPredictions]);

  return (
    <div className='tw-w-full tw-mt-0 tw-flex tw-flex-col'>
      <div className='tw-w-full tw-mt-2 tw-flex tw-mb-4 tw-group tw-relative'>
        <Search className='tw-absolute tw-left-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-grey-400 tw-w-6 tw-h-6 tw-aspect-square tw-pointer-events-none group-focus-within:tw-text-base-green' />
        <input
          ref={inputFieldRef}
          type='text'
          value={inputValue}
          placeholder='Search Customer Address (enter at least 3 characters)'
          autoComplete='off'
          className='tw-px-5 tw-pl-12 tw-py-5 tw-rounded-2xl tw-bg-grey-100 tw-outline-0 tw-w-full tw-border-solid tw-border-r-2 tw-border-y-2 tw-border-grey-300 focus:tw-border-base-green
          focus:tw-outline focus:tw-outline-2 focus:tw-outline-green-700/20'
          onChange={onInputChange}
          data-dd-privacy='mask'
        />
      </div>

      {minInputError ? (
        <div className='tw-font-medium tw-text-[1.3rem] tw-text-right tw-text-red-600 tw-leading-none'>
          {minInputError}
        </div>
      ) : null}

      {predictionsLoading && (
        <Skeleton
          rows={[
            { cols: 1, height: '3rem' },
            { cols: 1, height: '3rem' },
            { cols: 1, height: '3rem' },
            { cols: 1, height: '3rem' },
          ]}
        />
      )}

      {predictions.length > 0 && (
        <div className='tw-z-50 tw-left-0 tw-top-full tw-w-full'>
          <ul className='tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-grey-200 tw-overflow-hidden'>
            {predictions.map((p, index) => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={p.place_id}
                className={`tw-p-4 tw-cursor-pointer hover:tw-bg-grey-100 tw-border tw-border-solid !tw-border-x-0 !tw-border-t-0 tw-border-grey-200 tw-font-medium tw-text-zinc-600${
                  index === predictions.length - 1 ? ' !tw-border-b-0' : ''
                }`}
                onClick={() => onSelectPrediction(p.place_id, p.description)}
                data-dd-privacy='mask'
              >
                {p.description}
              </li>
            ))}
          </ul>
        </div>
      )}

      {addressInstruction && (
        <div className='tw-flex tw-gap-2 tw-text-200 tw-font-medium tw-text-[#d02a38] tw-items-center'>
          <Info size={14} />
          {addressInstruction}
        </div>
      )}
    </div>
  );
};
